import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CalendarDays, Send, HandHeart, ArrowRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { PAGES } from "@/i18n/extra_translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PastEvents = () => {
  const { t, lang } = useLanguage();
  const pg = PAGES[lang] || PAGES.en;
  const [events, setEvents] = useState([]);

  useEffect(() => {
    axios.get(`${API}/past-events`).then((r) => setEvents(r.data.items || [])).catch(() => {});
  }, []);

  return (
    <main className="pt-28 pb-20 px-6" data-testid="past-events-page">
      <div className="max-w-7xl mx-auto lg:px-4">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl"
        >
          <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#1A2F24]" data-testid="past-events-title">{pg.pe_title}</h1>
          <p className="mt-6 text-base leading-relaxed text-[#5C7368]" data-testid="past-events-intro">{pg.pe_intro}</p>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="past-events-grid">
          {events.length === 0 && <p className="text-sm text-[#5C7368] col-span-full">Events coming soon.</p>}
          {events.map((ev, i) => {
            const hasGallery = ev.photos && ev.photos.length > 0;
            const inner = (
              <>
                {hasGallery ? (
                  <div className="h-60 overflow-hidden">
                    <img src={ev.cover} alt={ev.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                ) : (
                  <div className="h-60 bg-[#1E352A] flex items-center justify-center">
                    <CalendarDays size={40} className="text-[#C4D7D1]" />
                  </div>
                )}
                <div className="p-6">
                  <h3 className="font-display text-2xl text-[#1A2F24]">{ev.title}</h3>
                  <p className="mt-2 flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-[#F4A261]">
                    <CalendarDays size={13} /> {ev.date}
                  </p>
                  <p className="mt-3 text-sm leading-relaxed text-[#5C7368] line-clamp-2">{ev.description}</p>
                  {hasGallery && (
                    <p className="mt-4 inline-flex items-center gap-1.5 text-xs tracking-[0.12em] uppercase text-[#1E352A] group-hover:text-[#F4A261] transition-colors duration-300">
                      {pg.gallery_hint} <ArrowRight size={13} />
                    </p>
                  )}
                </div>
              </>
            );
            return (
              <motion.div
                key={ev.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              >
                {hasGallery ? (
                  <Link to={`/past-events/${ev.id}`} data-testid={`past-event-card-${i}`}
                    className="group block rounded-2xl border border-[#E2E8E4] bg-white/60 overflow-hidden transition-all duration-500 hover:border-[#F4A261]/60 hover:-translate-y-1">
                    {inner}
                  </Link>
                ) : (
                  <div data-testid={`past-event-card-${i}`}
                    className="group block rounded-2xl border border-[#E2E8E4] bg-white/60 overflow-hidden">
                    {inner}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-16 rounded-3xl bg-[#1E352A] p-8 sm:p-10 text-center"
          data-testid="past-events-cta"
        >
          <p className="font-display text-2xl sm:text-3xl text-[#FAF9F6] max-w-2xl mx-auto">{pg.pe_bottom}</p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <a href="/#contact" data-testid="past-events-contact-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#F4A261] text-white px-7 py-3.5 text-sm tracking-[0.12em] uppercase font-semibold transition-colors duration-300 hover:bg-[#C4D7D1] hover:text-[#1E352A]">
              <Send size={15} /> {pg.contact_us}
            </a>
            <a href="/#volunteer" data-testid="past-events-volunteer-btn"
              className="inline-flex items-center gap-2 rounded-full border border-[#C4D7D1]/40 text-[#FAF9F6] px-7 py-3.5 text-sm tracking-[0.12em] uppercase transition-colors duration-300 hover:border-[#F4A261] hover:text-[#F4A261]">
              <HandHeart size={16} /> {t("nav_volunteer")}
            </a>
          </div>
        </motion.div>
      </div>
    </main>
  );
};

export default PastEvents;
